function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from "styled-components";
import { useMemo, useRef, useCallback, useEffect } from "react";
import Downshift from "downshift";
import { Text } from "sprout-ui-primitives";
import Fuse from "fuse.js";
import CSSTransitionPortal from "sprout-ui-dom/dist/Portal/CSSTransitionPortal";
import { useLocation, useHistory } from "react-router-dom";
import OutsideClickListener from "sprout-ui-dom/dist/internal/OutsideClickListener";
import { useJumpTo } from "@sprout/providers/dist/jumpTo";
import { media } from "sprout-ui-theme";
import { Search } from "sprout-ui-icons";
import { stringify } from "query-string";
import { hasCommandModifier } from "sprout-lib";
import { useActionRegistryGlobalHandler, useNavigationListContext } from "@sprout/list-navigation";
import { getIsLargePointerDevice } from "sprout-ui-dom/dist/utils";
import { SearchScope } from "../Search/types";
import { JumpToWrapper, JumpToScrollContainer } from "./styles";
import JumpToInput from "./JumpTo_Input";
import useJumpToGroups from "./useJumpToGroups";
import JumpToGroups from "./JumpToGroups";
/**
 * A function to filter each groups based on the result provided by `Fuse.js`
 */ const processFuseResult = (results)=>{
    return results.map((result)=>{
        var ref;
        const matches = ((ref = result.matches) === null || ref === void 0 ? void 0 : ref.map(({ refIndex  })=>{
            return refIndex;
        })) || [];
        const { item  } = result;
        const { group , items  } = item;
        return {
            group,
            items: items.filter((_, index)=>matches.includes(index))
        };
    });
};
const StyledText = styled(Text)`
  ${media.target("touch")} {
    display: none;
  }
`;
const JumpTo = ()=>{
    const [jumpTo, openJumpTo, closeJumpTo] = useJumpTo();
    useActionRegistryGlobalHandler("OPEN_JUMP_TO", useCallback(()=>{
        openJumpTo();
        return true;
    }, [
        openJumpTo
    ]), {
        /**
       * required in Firefox, as `cmd+j` opens the `Downloads` window
       */ preventDefault: true
    });
    const { pathname  } = useLocation();
    const jumpToGroups = useJumpToGroups();
    const groups = useMemo(()=>{
        return jumpToGroups.map(({ group , items  })=>{
            if (group === "RECENT") return {
                group,
                items: items.filter((item)=>item.url !== pathname)
            };
            return {
                group,
                items
            };
        });
    }, [
        jumpToGroups,
        pathname
    ]);
    const searcher = useMemo(()=>{
        const options = {
            includeMatches: true,
            keys: [
                "items.label"
            ],
            threshold: 0.1,
            findAllMatches: true
        };
        return new Fuse(groups, options);
    }, [
        groups
    ]);
    const history = useHistory();
    const commandRef = useRef(false);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { dispatchCommand  } = useNavigationListContext();
    const handleChange = useCallback((selectedItem, stateAndHelpers)=>{
        if (!selectedItem) return;
        const { url , action  } = selectedItem;
        if (commandRef.current) {
            window.open(url, "blank");
            // reset;
            commandRef.current = false;
        } else if (pathname !== url) {
            if (url) history.push(url);
            else if (action) dispatchCommand(action, null);
        }
        closeJumpTo();
        /**
       * `reset` works better here instead of `clearSelection`
       * As the component unmounts, the internal state reverts to the default
       * By using `reset`, we don't have to handle `null` item in `onChange`
       */ stateAndHelpers.reset();
    }, [
        pathname,
        closeJumpTo,
        dispatchCommand,
        history
    ]);
    const inputRef = useRef(null);
    useEffect(()=>{
        var ref;
        if (jumpTo && getIsLargePointerDevice()) (ref = inputRef.current) === null || ref === void 0 ? void 0 : ref.focus();
    }, [
        jumpTo
    ]);
    return /*#__PURE__*/ _jsx(CSSTransitionPortal, {
        open: jumpTo,
        timeout: {
            enter: 200,
            exit: 150
        },
        children: /*#__PURE__*/ _jsx(Downshift, {
            defaultHighlightedIndex: 0,
            defaultIsOpen: true,
            itemToString: ()=>"",
            onChange: handleChange,
            children: ({ getInputProps , getRootProps , getMenuProps , getItemProps , inputValue ,  })=>{
                const groupsToRender = inputValue ? [
                    ...processFuseResult(searcher.search(inputValue.trim())),
                    {
                        group: "NO MATCH",
                        items: [
                            {
                                type: "APP",
                                icon: Search,
                                label: "Search in My Trees",
                                url: `/search/${SearchScope["my-trees"]}?${stringify({
                                    q: inputValue
                                })}`
                            }, 
                        ]
                    }, 
                ] : groups;
                return /*#__PURE__*/ _jsx(OutsideClickListener, {
                    onOutsideClick: closeJumpTo,
                    children: /*#__PURE__*/ _jsxs(JumpToWrapper, _objectSpread({
                        id: "JumpTo"
                    }, getRootProps(undefined, {
                        suppressRefError: true
                    }), {
                        children: [
                            /*#__PURE__*/ _jsxs(StyledText, {
                                fontSize: 1,
                                color: "white.70",
                                textAlign: "center",
                                mb: "16px",
                                children: [
                                    "Press",
                                    /*#__PURE__*/ _jsx(Text, {
                                        as: "span",
                                        fontWeight: "bold",
                                        children: ` H `
                                    }),
                                    "to open this menu"
                                ]
                            }),
                            /*#__PURE__*/ _jsx(JumpToInput, _objectSpread({
                                placeholder: "Type to visit a tree or recently visited page..."
                            }, getInputProps({
                                onKeyDown (event) {
                                    if (event.key === "Escape") closeJumpTo();
                                    if (hasCommandModifier(event)) commandRef.current = true;
                                }
                            }), {
                                /*
                   * The below are to appease TypeScript - it seems that
                   * getInputProps returns ref/as types that are too broad for
                   * <JumpToInput />
                   */ ref: inputRef,
                                as: undefined
                            })),
                            /*#__PURE__*/ _jsx(JumpToScrollContainer, _objectSpread({}, getMenuProps(), {
                                children: /*#__PURE__*/ _jsx(JumpToGroups, {
                                    collapsible: !inputValue,
                                    groups: groupsToRender,
                                    getItemProps: getItemProps
                                })
                            }))
                        ]
                    }))
                });
            }
        })
    });
};
export default JumpTo;
